import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Plus, Save, Trash2, RotateCcw, Search, Users, LayoutGrid, ListChecks, X } from "lucide-react";
import { api, formatErr } from "../lib/api";

const muted = { color: "var(--c-text-muted)" };
const border = { borderColor: "var(--c-border)" };

function groupScreens(screens) {
  const out = [];
  screens.forEach((s) => {
    const g = out.find((x) => x.name === s.group);
    if (g) g.items.push(s); else out.push({ name: s.group, items: [s] });
  });
  return out;
}

function Badge({ children, tone = "muted" }) {
  const tones = {
    muted: { background: "var(--c-bg, #f1f5f9)", color: "var(--c-text-muted)" },
    accent: { background: "var(--c-accent-soft)", color: "var(--c-accent)" },
  };
  return <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded" style={tones[tone]}>{children}</span>;
}

// Master–detail editor for access roles: pick a role on the left, tick its screens on the right.
export default function RoleAccessMatrix({ canEdit, onRolesChanged }) {
  const [screens, setScreens] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState(null); // { name, screens:Set } for selected role
  const [filter, setFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", copyFrom: "" });
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("editor");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [moveTo, setMoveTo] = useState("none");

  const groups = useMemo(() => groupScreens(screens), [screens]);
  const selected = roles.find((r) => r.id === selectedId);

  async function load(keepId) {
    try {
      const [s, r] = await Promise.all([api.get("/access-management/screens"), api.get("/access-management/roles")]);
      const list = r.data.roles || [];
      setScreens(s.data.screens || []);
      setRoles(list);
      onRolesChanged?.(list);
      const id = list.some((x) => x.id === keepId) ? keepId : list[0]?.id || "";
      setSelectedId(id);
      const role = list.find((x) => x.id === id);
      setDraft(role ? { name: role.name, screens: new Set(role.screens || []) } : null);
    } catch (err) {
      toast.error(formatErr(err));
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = !!(selected && draft && (
    draft.name.trim() !== selected.name ||
    draft.screens.size !== (selected.screens || []).length ||
    (selected.screens || []).some((k) => !draft.screens.has(k))
  ));

  function select(role) {
    if (role.id === selectedId) return;
    if (dirty && !window.confirm("Discard unsaved changes to this role?")) return;
    setSelectedId(role.id);
    setDraft({ name: role.name, screens: new Set(role.screens || []) });
    setConfirmDelete(false);
  }

  function toggle(keys, on) {
    setDraft((d) => {
      const next = new Set(d.screens);
      keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
      return { ...d, screens: next };
    });
  }

  async function save() {
    try {
      setSaving(true);
      await api.put(`/access-management/roles/${selected.id}`, { name: draft.name.trim(), screens: [...draft.screens] });
      toast.success(`Saved "${draft.name.trim()}"`);
      await load(selected.id);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  }

  async function createRole(e) {
    e.preventDefault();
    const source = roles.find((r) => r.id === newRole.copyFrom);
    try {
      const res = await api.post("/access-management/roles", { name: newRole.name.trim(), screens: source ? source.screens : ["home"] });
      toast.success("Role created");
      setNewRole({ name: "", copyFrom: "" });
      setCreating(false);
      await load(res.data.role.id);
    } catch (err) {
      toast.error(formatErr(err));
    }
  }

  async function deleteRole() {
    try {
      const params = selected.user_count > 0 ? { reassign_to: moveTo } : {};
      await api.delete(`/access-management/roles/${selected.id}`, { params });
      toast.success(`Role "${selected.name}" deleted. Restore it from the Deleted Roles tab.`);
      setConfirmDelete(false);
      setMoveTo("none");
      await load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  }

  const visibleRoles = useMemo(() => {
    const q = filter.toLowerCase().trim();
    return q ? roles.filter((r) => r.name.toLowerCase().includes(q)) : roles;
  }, [roles, filter]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[12.5px]" style={muted}>
          Choose which analytics screens each ERP role can open. Super Admin always has every screen.
        </div>
        <div className="inline-flex rounded-lg border p-0.5 shrink-0" style={border}>
          {[["editor", "Edit roles", ListChecks], ["matrix", "Overview", LayoutGrid]].map(([id, label, Icon]) => (
            <button key={id} type="button" onClick={() => setView(id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium"
              style={view === id ? { background: "var(--c-accent)", color: "#fff" } : muted}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
      </div>

      {view === "matrix" ? (
        <OverviewMatrix roles={visibleRoles} groups={groups} filter={filter} setFilter={setFilter}
          onPick={(r) => { select(r); setView("editor"); }} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-4">
          {/* Role list */}
          <div className="rounded-xl border flex flex-col" style={{ ...border, maxHeight: "72vh" }}>
            <div className="p-3 border-b space-y-2" style={border}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-[13px]">Roles <span style={muted}>({roles.length})</span></div>
                {canEdit && (
                  <button type="button" className="btn btn-outline !py-1 !px-2 text-[12px]" onClick={() => setCreating((v) => !v)}>
                    {creating ? <X size={13} /> : <Plus size={13} />} {creating ? "Cancel" : "New role"}
                  </button>
                )}
              </div>
              {creating && (
                <form onSubmit={createRole} className="space-y-2">
                  <input className="input w-full" autoFocus placeholder="Role name" value={newRole.name}
                    onChange={(e) => setNewRole((n) => ({ ...n, name: e.target.value }))} />
                  <select className="input w-full" value={newRole.copyFrom}
                    onChange={(e) => setNewRole((n) => ({ ...n, copyFrom: e.target.value }))}>
                    <option value="">Start with Home only</option>
                    {roles.map((r) => <option key={r.id} value={r.id}>Copy screens from {r.name}</option>)}
                  </select>
                  <button type="submit" className="btn btn-primary w-full justify-center" disabled={!newRole.name.trim()}>Create role</button>
                </form>
              )}
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={muted} />
                <input className="input w-full !pl-8" placeholder="Search roles" value={filter} onChange={(e) => setFilter(e.target.value)} />
              </div>
            </div>
            <div className="overflow-y-auto">
              {visibleRoles.map((r) => {
                const active = r.id === selectedId;
                return (
                  <button key={r.id} type="button" onClick={() => select(r)}
                    className="w-full text-left px-3 py-2.5 border-b flex items-start justify-between gap-2"
                    style={{ ...border, background: active ? "var(--c-accent-soft)" : "transparent", boxShadow: active ? "inset 3px 0 0 var(--c-accent)" : "none" }}>
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium truncate" title={r.name}>{r.name}</div>
                      <div className="text-[11.5px] mt-0.5" style={muted}>
                        {(r.screens || []).length} screens · {r.user_count || 0} users
                      </div>
                    </div>
                    <Badge tone={r.source === "erp" ? "muted" : "accent"}>{r.source === "erp" ? "ERP" : "Custom"}</Badge>
                  </button>
                );
              })}
              {!visibleRoles.length && <div className="p-4 text-[12.5px]" style={muted}>No roles match.</div>}
            </div>
          </div>

          {/* Role detail */}
          {selected && draft ? (
            <div className="rounded-xl border flex flex-col" style={border}>
              <div className="p-4 border-b flex flex-col md:flex-row md:items-start md:justify-between gap-3" style={border}>
                <div className="min-w-0 flex-1">
                  {canEdit ? (
                    <input className="input font-heading text-[16px] font-semibold w-full max-w-[420px]" value={draft.name}
                      onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} aria-label="Role name" />
                  ) : (
                    <div className="font-heading text-[16px] font-semibold">{selected.name}</div>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-[12px]" style={muted}>
                    <Badge tone={selected.source === "erp" ? "muted" : "accent"}>{selected.source === "erp" ? `ERP role #${selected.erp_role_id}` : "Custom role"}</Badge>
                    <span className="inline-flex items-center gap-1"><Users size={12} /> {selected.user_count || 0} users</span>
                    <span>· {draft.screens.size} of {screens.length} screens</span>
                  </div>
                  {selected.erp_menus?.length > 0 && (
                    <div className="mt-2 text-[11.5px]" style={muted}>
                      <span className="font-medium">ERP menus:</span> {selected.erp_menus.join(", ")}
                    </div>
                  )}
                </div>
                {canEdit && (
                  <div className="flex gap-2 shrink-0">
                    <button type="button" className="btn btn-outline" onClick={() => setConfirmDelete((v) => !v)} title="Delete role">
                      <Trash2 size={14} />
                    </button>
                    <button type="button" className="btn btn-outline" disabled={!dirty}
                      onClick={() => setDraft({ name: selected.name, screens: new Set(selected.screens || []) })}>
                      <RotateCcw size={14} /> Discard
                    </button>
                    <button type="button" className="btn btn-primary" disabled={!dirty || saving || !draft.name.trim()} onClick={save}>
                      <Save size={14} /> {saving ? "Saving…" : "Save"}
                    </button>
                  </div>
                )}
              </div>

              {confirmDelete && canEdit && (
                <div className="px-4 py-3 border-b flex flex-col md:flex-row md:items-center gap-3 text-[12.5px]"
                  style={{ ...border, background: "#fef2f2", color: "#991b1b" }}>
                  <div className="flex-1">
                    Delete <b>{selected.name}</b>? It moves to Deleted Roles and can be restored.
                    {selected.user_count > 0 && <> It is assigned to {selected.user_count} user(s), including inactive ones. Move them to:</>}
                  </div>
                  {selected.user_count > 0 && (
                    <select className="input md:w-[260px]" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                      <option value="none">No access role (full access by system role)</option>
                      {roles.filter((r) => r.id !== selected.id).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  )}
                  <div className="flex gap-2">
                    <button type="button" className="btn btn-outline" onClick={() => setConfirmDelete(false)}>Cancel</button>
                    <button type="button" className="btn btn-primary" style={{ background: "#dc2626", borderColor: "#dc2626" }} onClick={deleteRole}>
                      <Trash2 size={14} /> Delete role
                    </button>
                  </div>
                </div>
              )}

              <div className="p-4 grid grid-cols-1 xl:grid-cols-2 gap-4 overflow-y-auto" style={{ maxHeight: "60vh" }}>
                {groups.map((g) => {
                  const keys = g.items.map((s) => s.key);
                  const count = keys.filter((k) => draft.screens.has(k)).length;
                  return (
                    <div key={g.name} className="rounded-lg border" style={border}>
                      <label className="flex items-center justify-between px-3 py-2 border-b cursor-pointer"
                        style={{ ...border, background: "var(--c-bg, #f8fafc)" }}>
                        <span className="text-[11.5px] font-semibold uppercase tracking-wide" style={muted}>
                          {g.name} <span className="font-normal">· {count}/{keys.length}</span>
                        </span>
                        <span className="flex items-center gap-1.5 text-[11.5px]" style={muted}>
                          All
                          <input type="checkbox" disabled={!canEdit} checked={count === keys.length}
                            ref={(el) => { if (el) el.indeterminate = count > 0 && count < keys.length; }}
                            onChange={(e) => toggle(keys, e.target.checked)} />
                        </span>
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2">
                        {g.items.map((s) => {
                          const on = draft.screens.has(s.key);
                          return (
                            <label key={s.key} className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[12.5px] cursor-pointer"
                              style={{ background: on ? "var(--c-accent-soft)" : "transparent" }}>
                              <input type="checkbox" disabled={!canEdit} checked={on} onChange={(e) => toggle([s.key], e.target.checked)} />
                              <span>{s.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {dirty && (
                <div className="px-4 py-2 border-t text-[12px]" style={{ ...border, background: "#fff7e6", color: "#8a5a00" }}>
                  Unsaved changes. Users with this role see the change after their next page refresh.
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl border p-8 text-center text-[13px]" style={{ ...border, ...muted }}>
              {roles.length ? "Select a role to edit its screens." : "No access roles yet. Create one to get started."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Read-only screens x roles grid for comparing roles at a glance; click a role to edit it.
function OverviewMatrix({ roles, groups, filter, setFilter, onPick }) {
  const cell = { padding: "7px 10px", borderBottom: "1px solid var(--c-border)", textAlign: "center", whiteSpace: "nowrap" };
  const first = { ...cell, position: "sticky", left: 0, zIndex: 1, background: "var(--c-card, #fff)", textAlign: "left", minWidth: 190 };
  return (
    <div className="space-y-2">
      <div className="relative w-full md:w-[280px]">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={muted} />
        <input className="input w-full !pl-8" placeholder="Filter roles" value={filter} onChange={(e) => setFilter(e.target.value)} />
      </div>
      <div className="overflow-auto rounded-xl border" style={{ ...border, maxHeight: "70vh" }}>
        <table className="text-[12.5px]" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr>
              <th style={{ ...first, top: 0, zIndex: 3, fontSize: 11, ...muted }}>SCREEN</th>
              <th style={{ ...cell, position: "sticky", top: 0, zIndex: 2, background: "var(--c-accent-soft)", fontSize: 11 }}>SUPER ADMIN</th>
              {roles.map((r) => (
                <th key={r.id} style={{ ...cell, position: "sticky", top: 0, zIndex: 2, background: "var(--c-card, #fff)", fontSize: 11 }}>
                  <button type="button" className="uppercase font-semibold hover:underline" title="Edit this role" onClick={() => onPick(r)}>{r.name}</button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.name}>
                <tr>
                  <td style={{ ...first, fontSize: 11, fontWeight: 600, ...muted, background: "var(--c-bg, #f8fafc)" }}>{g.name.toUpperCase()}</td>
                  <td colSpan={roles.length + 1} style={{ ...cell, background: "var(--c-bg, #f8fafc)" }} />
                </tr>
                {g.items.map((s) => (
                  <tr key={s.key}>
                    <td style={first}>{s.label}</td>
                    <td style={{ ...cell, background: "var(--c-accent-soft)" }}><Check size={14} style={{ color: "var(--c-accent)", display: "inline" }} /></td>
                    {roles.map((r) => (
                      <td key={r.id} style={cell}>
                        {(r.screens || []).includes(s.key)
                          ? <Check size={14} style={{ color: "var(--c-accent)", display: "inline" }} />
                          : <span style={{ color: "var(--c-border)" }}>–</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
